/**
 * The Kapitan brand, from brief 10 (`Vizual tizim`, `k3-brand.json`): the logo, the AI mark, the
 * route line, the eight illustrations, the loader and the no-photo plate. Every colour in the
 * SVGs is a token, so dark mode and the dark rail recolour them without a second copy.
 */
import type { CSSProperties, JSX, ReactNode } from "react";

import { useTranslation } from "@/i18n";
import { cn } from "@/lib/cn";

export type LogoVariant = "K" | "A" | "B" | "C";
export type LogoKind = "full" | "mark" | "mono" | "dark";

/** The chosen logo: the owner kept the orange K tile (D612). Brief 10's three stay one word away. */
export const BRAND_LOGO: LogoVariant = "K";

interface MarkColours {
  ink: string;
  accent: string;
  /** Plate ground and the strokes on it: variant C only. */
  plate: string;
  onPlate: string;
}

const LIGHT: MarkColours = {
  ink: "var(--k-logo-ink)",
  accent: "var(--k-accent)",
  plate: "var(--k-navy)",
  onPlate: "var(--k-logo-on-dark)",
};
const MONO: MarkColours = { ink: "currentColor", accent: "currentColor", plate: "currentColor", onPlate: "var(--k-surface)" };
const DARK: MarkColours = {
  ink: "var(--k-logo-on-dark)",
  accent: "var(--k-logo-accent-on-dark)",
  plate: "var(--k-logo-on-dark)",
  onPlate: "var(--k-navy)",
};

function MarkPaths({ variant, colours: c }: { variant: LogoVariant; colours: MarkColours }): JSX.Element {
  if (variant === "K") {
    // Drawn on a 32 box, the shape the product has always had.
    return (
      <g transform="scale(0.75)" strokeWidth="2.4">
        <rect width="32" height="32" rx="8" fill={c.accent} stroke="none" />
        <path d="M10 8.5v15" stroke="var(--k-logo-on-dark)" />
        <path d="M22 9.5 13.5 16l8.5 6.5" stroke="var(--k-logo-on-dark)" />
      </g>
    );
  }
  if (variant === "B") {
    return (
      <>
        <circle cx="12" cy="12" r="10" stroke={c.ink} />
        <path d="M9 6.8v10.4" stroke={c.ink} />
        <path d="M9.4 12.3 16 7.4l-3.8 5.7z" fill={c.accent} stroke={c.accent} />
        <path d="m12.1 13 4.1 4.3" stroke={c.ink} />
      </>
    );
  }
  if (variant === "C") {
    return (
      <>
        <rect x="1" y="1" width="22" height="22" rx="6" fill={c.plate} stroke="none" />
        <path d="M8.3 6.5v11" stroke={c.onPlate} />
        <path d="M8.7 12.3 16.4 7l-4.5 6.2z" fill={c.accent} stroke={c.accent} />
        <path d="m11.9 13.2 4.6 4.5" stroke={c.onPlate} />
      </>
    );
  }
  return (
    <>
      <path d="M6.5 3.5v17" stroke={c.ink} />
      <path d="M7 12.4 18.6 4.2l-6.9 9.4z" fill={c.accent} stroke={c.accent} />
      <path d="m11.6 13.6 6.9 6.9" stroke={c.ink} />
    </>
  );
}

export interface LogoProps {
  variant?: LogoVariant | undefined;
  kind?: LogoKind | undefined;
  /** The mark's side in px. The word in `full` follows it. */
  size?: number | undefined;
  className?: string | undefined;
}

/** `full` is the mark and the word; `mark`, `mono` and `dark` are the mark alone. */
export function Logo({ variant = BRAND_LOGO, kind = "full", size = 24, className }: LogoProps): JSX.Element {
  const { t } = useTranslation();
  const colours = kind === "mono" ? MONO : kind === "dark" ? DARK : LIGHT;
  const mark = (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      role={kind === "full" ? undefined : "img"}
      aria-hidden={kind === "full" ? true : undefined}
      aria-label={kind === "full" ? undefined : t("app.name")}
      className={cn("block shrink-0", kind === "full" ? undefined : className)}
    >
      <MarkPaths variant={variant} colours={colours} />
    </svg>
  );
  if (kind !== "full") return mark;
  return (
    <span className={cn("inline-flex items-center gap-2.5 text-ink", className)}>
      {mark}
      <span className={cn("font-semibold tracking-kpi", size >= 28 ? "text-h2" : "text-lg")}>{t("app.name")}</span>
    </span>
  );
}

export interface KapitanAiMarkProps {
  size?: number | undefined;
  className?: string | undefined;
  /** Spoken name. Omit beside words that already say it. */
  label?: string | undefined;
}

/** The compass with a spark: every place the product marks machine-written work. */
export function KapitanAiMark({ size = 16, className, label }: KapitanAiMarkProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      role={label === undefined ? undefined : "img"}
      aria-label={label}
      aria-hidden={label === undefined ? true : undefined}
      className={cn("block shrink-0 text-ai-line", className)}
    >
      <circle cx="12" cy="12" r="9.5" />
      <path d="M12 6.6l1.4 4 4 1.4-4 1.4-1.4 4-1.4-4-4-1.4 4-1.4z" />
    </svg>
  );
}

/* ---------------------------------------------------------------- route line */

/** Dashed 1.5px, 4 on, 4 off; passed in terracotta, the rest in sand. */
function dash(passed: boolean, vertical: boolean, onNavy = false): CSSProperties {
  const colour = onNavy ? "var(--k-logo-accent-on-dark)" : passed ? "var(--k-accent)" : "var(--k-route-rest)";
  return {
    [vertical ? "width" : "height"]: "1.5px",
    background: `repeating-linear-gradient(${vertical ? "180deg" : "90deg"},${colour} 0 4px,transparent 4px 8px)`,
  };
}

const DOT = "block size-2 shrink-0 rounded-pill";

/** The 8px dot of step `index`. Without a `current` the line is a leg: ring start, filled end. */
function RouteDot({
  index,
  last,
  current,
  onNavy,
  large,
}: {
  index: number;
  last: number;
  current: number | undefined;
  onNavy: boolean;
  large: boolean;
}): JSX.Element {
  if (onNavy) {
    return index === 0 ? (
      <span className={cn(DOT, "border-[1.5px] border-btn-primary-ink")} />
    ) : (
      <span className={DOT} style={{ background: "var(--k-logo-accent-on-dark)" }} />
    );
  }
  const DOT_SIZED = large ? "block size-2.5 shrink-0 rounded-pill" : DOT;
  if (current === undefined) {
    if (index === 0) return <span className={cn(DOT_SIZED, "border-[1.5px] border-navy bg-surface")} />;
    if (index === last) return <span className={cn(DOT_SIZED, "bg-accent")} />;
    return <span className={cn(DOT_SIZED, "border-[1.5px] border-accent bg-surface")} />;
  }
  if (index < current) return <span className={cn(DOT_SIZED, "bg-accent")} />;
  if (index === current) {
    return <span className={cn(DOT_SIZED, "border-[1.5px] border-accent bg-surface")} style={{ boxShadow: "var(--k-halo)" }} />;
  }
  return <span className={cn(DOT_SIZED, "border-[1.5px] border-route-rest bg-surface")} />;
}

export interface RouteLineProps {
  /** Labels, one per dot. Empty strings draw the dot with no label. */
  steps: readonly ReactNode[];
  /** The step reached. Omit for a leg between two ends (a flight, a cruise's ports). */
  current?: number | undefined;
  orientation?: "horizontal" | "vertical" | undefined;
  /** The leg drawn on the navy brand panel: ring start, soft terracotta dots and dashes. */
  onNavy?: boolean | undefined;
  /** 10px dots, as the onboarding wizard draws its stages. */
  large?: boolean | undefined;
  className?: string | undefined;
}

/** The signature element: the same line everywhere a thing moves from one place to another. */
export function RouteLine({
  steps,
  current,
  orientation = "horizontal",
  onNavy = false,
  large = false,
  className,
}: RouteLineProps): JSX.Element {
  const last = steps.length - 1;
  const passedUpTo = current ?? last;
  if (orientation === "vertical") {
    return (
      <ol className={cn("flex flex-col", className)}>
        {steps.map((step, index) => (
          <li key={index} className="grid grid-cols-[16px_minmax(0,1fr)] gap-2.5">
            <span className="flex flex-col items-center">
              <span className="mt-1">
                <RouteDot index={index} last={last} current={current} onNavy={onNavy} large={large} />
              </span>
              {index === last ? null : <span className="min-h-3 flex-1" style={dash(index < passedUpTo, true)} />}
            </span>
            <span className={cn("min-w-0 text-sm", index === last ? null : "pb-3")}>{step}</span>
          </li>
        ))}
      </ol>
    );
  }
  const labelled = steps.some((step) => step !== "");
  return (
    <div className={cn("flex min-w-0 flex-col gap-2", className)}>
      <div className="flex items-center">
        {steps.map((_, index) => (
          <span key={index} className={cn("flex items-center", index === 0 ? "shrink-0" : "min-w-2 flex-1")}>
            {index === 0 ? null : <span className="min-w-2 flex-1" style={dash(index <= passedUpTo, false, onNavy)} />}
            <RouteDot index={index} last={last} current={current} onNavy={onNavy} large={large} />
          </span>
        ))}
      </div>
      {labelled ? (
        <div className={cn("grid text-xs", onNavy ? "text-navy-ink-muted" : "text-ink-subtle")} style={{ gridTemplateColumns: `repeat(${String(steps.length)},minmax(0,1fr))` }}>
          {steps.map((step, index) => (
            <span
              key={index}
              className={cn(
                "truncate",
                index === 0 ? "text-left" : index === last ? "text-right" : "text-center",
                index === current && "font-medium text-ink",
              )}
            >
              {step}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- illustrations */

export type IllustrationName = "chat" | "task" | "bron" | "none" | "err" | "deny" | "off" | "done";

const FLOOR = "var(--k-ill-floor)";
const TERRA = "var(--k-accent)";
const SOFT = "var(--k-accent-soft)";
const PAPER = "var(--k-surface)";
const POINT = "var(--k-ill-point)";

function Floor(): JSX.Element {
  return <path d="M16 80h88" stroke={FLOOR} strokeDasharray="3 4" />;
}

const ILLUSTRATIONS: Record<IllustrationName, JSX.Element> = {
  chat: (
    <>
      <Floor />
      <path d="M28 22h50a10 10 0 0 1 10 10v16a10 10 0 0 1-10 10H48l-12 10v-10h-8a10 10 0 0 1-10-10V32a10 10 0 0 1 10-10z" fill={PAPER} />
      <circle cx="42" cy="40" r="2" fill={TERRA} stroke="none" />
      <circle cx="53" cy="40" r="2" fill={TERRA} stroke="none" />
      <circle cx="64" cy="40" r="2" fill={TERRA} stroke="none" />
      <path d="M90 30c8-4 12-10 14-16" stroke={TERRA} strokeDasharray="3 3" />
      <path d="m98 12 10-4-3 10-2-4z" fill={SOFT} stroke={TERRA} />
    </>
  ),
  task: (
    <>
      <Floor />
      <rect x="38" y="16" width="44" height="58" rx="6" fill={PAPER} />
      <rect x="50" y="11" width="20" height="9" rx="3" fill={SOFT} stroke={TERRA} />
      <path d="M47 34h4M56 34h18M47 46h4M56 46h18M47 58h4M56 58h12" />
    </>
  ),
  bron: (
    <>
      <Floor />
      <path d="M22 28h76v10a7 7 0 0 0 0 14v10H22V52a7 7 0 0 0 0-14z" fill={PAPER} />
      <path d="M76 30v30" stroke={FLOOR} strokeDasharray="3 3" />
      <circle cx="34" cy="45" r="2.5" fill={POINT} stroke="none" />
      <path d="M38 45h20" stroke={TERRA} strokeDasharray="3 3" />
      <circle cx="62" cy="45" r="2.5" fill={TERRA} stroke="none" />
    </>
  ),
  none: (
    <>
      <Floor />
      <path d="M20 24l24-6 28 6 26-6v50l-26 6-28-6-24 6z" fill={PAPER} />
      <path d="M44 18v50M72 24v50" stroke={FLOOR} />
      <circle cx="78" cy="50" r="12" fill={SOFT} stroke={TERRA} />
      <path d="m87 59 10 10" stroke={TERRA} />
    </>
  ),
  err: (
    <>
      <Floor />
      <circle cx="58" cy="44" r="26" fill={PAPER} />
      <circle cx="58" cy="44" r="2" fill={POINT} stroke="none" />
      <path d="M58 44 66 24" stroke={TERRA} />
      <path d="M58 44 50 64" />
      <path d="M58 22v-4M58 70v-4M36 44h-4M84 44h-4" />
      <path d="m92 16 8 8M100 16l-8 8" stroke={TERRA} />
    </>
  ),
  deny: (
    <>
      <Floor />
      <path d="M50 30v-6a4 4 0 0 1 4-4h12a4 4 0 0 1 4 4v6" />
      <rect x="30" y="30" width="60" height="42" rx="6" fill={PAPER} />
      <path d="M42 30v42M78 30v42" stroke={FLOOR} />
      <rect x="53" y="47" width="14" height="11" rx="2" fill={SOFT} stroke={TERRA} />
      <path d="M56 47v-3a4 4 0 0 1 8 0v3" stroke={TERRA} />
    </>
  ),
  off: (
    <>
      <path d="M14 74c6-4 10-4 16 0s10 4 16 0 10-4 16 0 10 4 16 0 10-4 16 0 10 4 16 0" stroke={FLOOR} />
      <path d="M28 54h64l-9 14H37z" fill={PAPER} />
      <rect x="42" y="40" width="30" height="14" rx="2" fill={PAPER} />
      <path d="M54 40v-8h8v8" stroke={TERRA} fill={SOFT} />
      <path d="M86 22a14 14 0 0 1 18 0M90 28a8 8 0 0 1 10 0" stroke={FLOOR} />
      <path d="m84 14 22 20" stroke={TERRA} />
    </>
  ),
  done: (
    <>
      <Floor />
      <circle cx="20" cy="66" r="3" fill={POINT} stroke="none" />
      <path d="M24 66c16 0 20-28 40-28s24 16 36-4" stroke={TERRA} strokeDasharray="3 4" />
      <path d="M100 34V12" />
      <path d="M100 12h14l-4 5 4 5h-14z" fill={SOFT} stroke={TERRA} />
      <circle cx="44" cy="28" r="10" fill={PAPER} />
      <path d="m39 28 4 4 7-8" stroke={TERRA} />
    </>
  ),
};

/** One family: 1.5px line, ink and terracotta, on a sand floor. Decorative: the words say it. */
export function Illustration({ name, width = 120, className }: { name: IllustrationName; width?: number | undefined; className?: string | undefined }): JSX.Element {
  return (
    <svg
      width={width}
      height={Math.round(width * 0.75)}
      viewBox="0 0 120 90"
      fill="none"
      stroke="var(--k-ill-ink)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cn("block shrink-0", className)}
    >
      {ILLUSTRATIONS[name]}
    </svg>
  );
}

/* ---------------------------------------------------------------- loading and the plate */

/** The full-page wait: the compass turns. The word is the caller's, under it. */
export function CompassLoader({ size = 32, label, className }: { size?: number | undefined; label?: string | undefined; className?: string | undefined }): JSX.Element {
  return (
    <span role="status" className={cn("inline-flex flex-col items-center gap-2.5", className)}>
      <KapitanAiMark size={size} className="k-compass text-accent" />
      {label === undefined ? null : <span className="text-sm text-ink-muted">{label}</span>}
    </span>
  );
}

/** Stands in for a photo that does not exist: the destination's name and the route line. */
export function BrandPlate({ destination, className }: { destination: string; className?: string | undefined }): JSX.Element {
  return (
    <div
      className={cn("relative flex h-33 flex-col justify-between overflow-hidden rounded-card p-3.5", className)}
      style={{ background: "linear-gradient(135deg,var(--k-plate-from) 0%,var(--k-plate-to) 100%)" }}
    >
      <b className="text-lg font-semibold" style={{ color: "var(--k-plate-ink)" }}>
        {destination}
      </b>
      <RouteLine steps={["", ""]} />
    </div>
  );
}
